import { supabase } from '../lib/supabase';

export const auditLogService = {
  /**
   * Fetch audit logs with optional filters
   * @param {Object} filters - Filter parameters
   * @returns {Promise<Array>} Array of audit logs in camelCase
   */
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          user_profiles!audit_logs_user_id_fkey(email, full_name),
          companies(company_name)
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.dateFrom) {
        query = query?.gte('created_at', new Date(filters.dateFrom)?.toISOString());
      }
      if (filters?.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate?.setHours(23, 59, 59, 999);
        query = query?.lte('created_at', endDate?.toISOString());
      }
      if (filters?.entityType && filters?.entityType !== 'all') {
        query = query?.eq('entity_type', filters?.entityType);
      }
      if (filters?.actorEmail) {
        // This requires a join, so we'll filter client-side
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform to camelCase and add computed fields
      const transformedData = data?.map(log => ({
        id: log?.id,
        timestamp: log?.created_at,
        actorEmail: log?.user_profiles?.email || 'System',actorName: log?.user_profiles?.full_name || 'System',company: log?.companies?.company_name || null,companyId: log?.company_id,eventCategory: this._categorizeAction(log?.action),action: log?.action,resourceType: log?.entity_type,resourceId: log?.entity_id,severity: this._determineSeverity(log?.action, log?.details),ipAddress: log?.ip_address,deviceInfo: log?.user_agent || 'Unknown',
        metadata: log?.details || {}
      }));

      // Client-side email filter if needed
      if (filters?.actorEmail) {
        return transformedData?.filter(log =>
          log?.actorEmail?.toLowerCase()?.includes(filters?.actorEmail?.toLowerCase())
        );
      }

      return transformedData;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  /**
   * Get a single audit log by ID
   * @param {string} id - Log ID
   * @returns {Promise<Object>} Audit log in camelCase
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.select(`*,user_profiles!audit_logs_user_id_fkey(email, full_name),companies(company_name)`)?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        timestamp: data?.created_at,
        actorEmail: data?.user_profiles?.email || 'System',actorName: data?.user_profiles?.full_name || 'System',company: data?.companies?.company_name || null,companyId: data?.company_id,eventCategory: this._categorizeAction(data?.action),action: data?.action,resourceType: data?.entity_type,resourceId: data?.entity_id,severity: this._determineSeverity(data?.action, data?.details),ipAddress: data?.ip_address,deviceInfo: data?.user_agent || 'Unknown',
        metadata: data?.details || {}
      };
    } catch (error) {
      console.error('Error fetching audit log:', error);
      throw error;
    }
  },

  /**
   * Categorize action into event type
   * @private
   */
  _categorizeAction(action) {
    const actionUpper = action?.toUpperCase();
    
    if (actionUpper?.includes('CREATE') || actionUpper?.includes('UPDATE') || 
        actionUpper?.includes('DELETE') || actionUpper?.includes('INSERT')) {
      return 'CRUD';
    }
    if (actionUpper?.includes('LOGIN') || actionUpper?.includes('LOGOUT') || 
        actionUpper?.includes('AUTH') || actionUpper?.includes('SIGNIN')) {
      return 'AUTH';
    }
    if (actionUpper?.includes('OWNERSHIP') || actionUpper?.includes('TRANSFER')) {
      return 'OWNERSHIP';
    }
    if (actionUpper?.includes('PAYMENT') || actionUpper?.includes('BILLING')) {
      return 'BILLING';
    }
    if (actionUpper?.includes('PERMISSION') || actionUpper?.includes('GRANT') || 
        actionUpper?.includes('REVOKE')) {
      return 'PERMISSION';
    }
    if (actionUpper?.includes('SYSTEM') || actionUpper?.includes('BACKUP')) {
      return 'SYSTEM';
    }
    
    return 'CRUD'; // Default
  },

  /**
   * Determine severity level based on action and details
   * @private
   */
  _determineSeverity(action, details) {
    const actionUpper = action?.toUpperCase();
    
    // Critical actions
    if (actionUpper?.includes('DELETE') || actionUpper?.includes('REVOKE') || 
        actionUpper?.includes('SUSPEND')) {
      return 'critical';
    }
    
    // Error actions
    if (actionUpper?.includes('FAILED') || actionUpper?.includes('ERROR')) {
      return 'error';
    }
    
    // Warning actions
    if (actionUpper?.includes('TRANSFER') || actionUpper?.includes('GRANT') || 
        actionUpper?.includes('MODIFY')) {
      return 'warning';
    }
    
    // Default to info
    return 'info';
  },

  /**
   * Export audit logs in specified format
   * @param {Array} logs - Logs to export
   * @param {string} format - Export format (csv, json)
   * @returns {string} Formatted data
   */
  async exportLogs(logs, format = 'csv') {
    if (format === 'csv') {
      const headers = [
        'Timestamp', 'Actor Email', 'Actor Name', 'Company', 'Event Category',
        'Action', 'Resource Type', 'Resource ID', 'Severity', 'IP Address'
      ];
      
      const rows = logs?.map(log => [
        log?.timestamp,
        log?.actorEmail,
        log?.actorName,
        log?.company || '-',
        log?.eventCategory,
        log?.action,
        log?.resourceType,
        log?.resourceId,
        log?.severity,
        log?.ipAddress
      ]);

      const csvContent = [
        headers?.join(','),
        ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
      ]?.join('\n');

      return csvContent;
    }

    if (format === 'json') {
      return JSON.stringify(logs, null, 2);
    }

    throw new Error(`Unsupported export format: ${format}`);
  }
};